/**
 * EnvironmentMonitorPage — run history, status, and results
 * for environment build pipelines.
 *
 * Accessible at:
 *   /environment/monitor         — all runs
 *   /theatres/:theatreId/monitor — filtered to a single theatre
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bot,
  Box,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  Dices,
  Download,
  ExternalLink,
  Fingerprint,
  Loader2,
  Play,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useEnvironmentRuns } from '../hooks/useEnvironmentRuns';
import { useEnvironmentAttestation } from '../hooks/useEnvironmentAttestation';
import { useRunSteps } from '../hooks/useRunSteps';
import { useRunModuleOutputs } from '../hooks/useRunModuleOutputs';
import { useTheatreDetail } from '../hooks/useTheatreDetail';
import type {
  AgentReadinessEvaluationOutput,
  CalibrationOutput,
  EnvironmentRun,
  EnvironmentRunModuleOutput,
  EnvironmentRunStatus,
  ForgeIntakeOutput,
  ParadoxScanOutput,
  RunStep,
  ScenarioPackEvaluationOutput,
  TheatreEvaluationOutput,
} from '../types/environmentRun';
import { ENV_TERMINAL_STATUSES } from '../types/environmentRun';

// ── Helpers ──────────────────────────────────────────────────────────────

type StatusFilter = 'all' | 'active' | 'completed' | 'failed';

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  const seconds = Math.floor(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Status badge ─────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  EnvironmentRunStatus,
  { label: string; dotClass: string; bgClass: string; textClass: string }
> = {
  QUEUED: {
    label: 'Queued',
    dotClass: 'bg-[var(--e-text-muted)]',
    bgClass: 'border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)]',
    textClass: 'text-[var(--e-text-muted)]',
  },
  INITIALISING: {
    label: 'Initialising',
    dotClass: 'bg-[var(--e-purple-500)] animate-pulse',
    bgClass: 'border-[var(--e-purple-200)] bg-[var(--e-purple-50)]',
    textClass: 'text-[var(--e-purple-700)]',
  },
  INGESTING: {
    label: 'Ingesting',
    dotClass: 'bg-[var(--e-purple-500)] animate-pulse',
    bgClass: 'border-[var(--e-purple-200)] bg-[var(--e-purple-50)]',
    textClass: 'text-[var(--e-purple-700)]',
  },
  EVALUATING: {
    label: 'Evaluating',
    dotClass: 'bg-[var(--e-orange-600)] animate-pulse',
    bgClass: 'border-[color:oklch(0.708_0.136_62_/_0.20)] bg-[color:oklch(0.708_0.136_62_/_0.10)]',
    textClass: 'text-[var(--e-orange-600)]',
  },
  SETTLING: {
    label: 'Settling',
    dotClass: 'bg-[var(--e-purple-500)] animate-pulse',
    bgClass: 'border-[var(--e-purple-200)] bg-[var(--e-purple-50)]',
    textClass: 'text-[var(--e-purple-700)]',
  },
  CERTIFYING: {
    label: 'Certifying',
    dotClass: 'bg-[var(--e-green-600)] animate-pulse',
    bgClass: 'border-[color:oklch(0.545_0.170_152_/_0.18)] bg-[var(--e-green-50)]',
    textClass: 'text-[var(--e-green-600)]',
  },
  COMPLETED: {
    label: 'Completed',
    dotClass: 'bg-[var(--e-green-600)]',
    bgClass: 'border-[color:oklch(0.545_0.170_152_/_0.18)] bg-[var(--e-green-50)]',
    textClass: 'text-[var(--e-green-600)]',
  },
  FAILED: {
    label: 'Failed',
    dotClass: 'bg-[var(--e-red-600)]',
    bgClass: 'border-[color:oklch(0.545_0.185_25_/_0.18)] bg-[var(--e-red-50)]',
    textClass: 'text-[var(--e-red-600)]',
  },
  CANCELLED: {
    label: 'Cancelled',
    dotClass: 'bg-[var(--e-text-disabled)]',
    bgClass: 'border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)]',
    textClass: 'text-[var(--e-text-muted)]',
  },
};

function StatusBadge({ status }: { status: EnvironmentRunStatus }) {
  const config = STATUS_CONFIG[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold',
        config.bgClass,
        config.textClass,
      )}
    >
      <span className={cn('h-2 w-2 rounded-full', config.dotClass)} />
      {config.label}
    </span>
  );
}

// ── Stat cards ───────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number | string;
  icon: React.ElementType;
  tone?: 'success' | 'warning' | 'danger' | 'purple';
}) {
  const iconClass =
    tone === 'success'
      ? 'text-[var(--e-green-600)]'
      : tone === 'warning'
        ? 'text-[var(--e-orange-600)]'
        : tone === 'danger'
          ? 'text-[var(--e-red-600)]'
          : tone === 'purple'
            ? 'text-[var(--e-purple-500)]'
            : 'text-[var(--e-text-muted)]';

  const valueClass =
    tone === 'success'
      ? 'text-[var(--e-green-600)]'
      : tone === 'danger'
        ? 'text-[var(--e-red-600)]'
        : 'text-[var(--e-text-primary)]';

  return (
    <div className="rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] px-4 py-3 shadow-[var(--e-shadow-xs)]">
      <div className="mb-1.5 flex items-center gap-1.5">
        <Icon className={cn('h-3.5 w-3.5', iconClass)} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
          {label}
        </span>
      </div>
      <div
        className={cn(
          'font-mono text-[24px] font-bold leading-8 tabular-nums',
          valueClass,
        )}
      >
        {value}
      </div>
    </div>
  );
}

// ── Progress bar ─────────────────────────────────────────────────────────

function ProgressBar({
  progress,
  total,
  status,
}: {
  progress: number;
  total: number;
  status: EnvironmentRunStatus;
}) {
  const pct = total > 0 ? Math.round((progress / total) * 100) : 0;
  const barColour =
    status === 'FAILED'
      ? 'bg-[var(--e-red-600)]'
      : status === 'COMPLETED'
        ? 'bg-[var(--e-green-600)]'
        : 'bg-[var(--e-purple-500)]';

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--e-bg-sunken)]">
        <div
          className={cn('h-full rounded-full transition-all duration-500', barColour)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 text-right font-mono text-[10px] text-[var(--e-text-muted)]">
        {pct}%
      </span>
    </div>
  );
}

// ── Step timeline (expanded detail) ──────────────────────────────────────

const STEP_STATUS_ICON: Record<string, { Icon: React.ElementType; className: string }> = {
  PASSED: { Icon: CheckCircle2, className: 'text-[var(--e-green-600)]' },
  FAILED: { Icon: XCircle, className: 'text-[var(--e-red-600)]' },
  RUNNING: { Icon: Loader2, className: 'text-[var(--e-purple-500)] animate-spin' },
  PENDING: { Icon: Clock, className: 'text-[var(--e-text-disabled)]' },
  SKIPPED: { Icon: ArrowRight, className: 'text-[var(--e-text-muted)]' },
};

function StepTimeline({ runId, runStatus }: { runId: string; runStatus: EnvironmentRunStatus }) {
  const { steps, isLoading } = useRunSteps(runId, runStatus);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--e-text-muted)]" />
        <span className="text-[12px] text-[var(--e-text-muted)]">Loading steps…</span>
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <div className="px-4 py-3 text-[12px] text-[var(--e-text-muted)]">
        {runStatus === 'QUEUED' ? 'Waiting to start…' : 'No step data recorded.'}
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {steps.map((step: RunStep, idx: number) => {
        const cfg = STEP_STATUS_ICON[step.status] ?? STEP_STATUS_ICON.PENDING;
        const isLast = idx === steps.length - 1;
        return (
          <div key={step.step_id} className="relative flex gap-3 px-4">
            {/* Vertical connector line */}
            <div className="flex flex-col items-center">
              <cfg.Icon className={cn('h-4 w-4 shrink-0', cfg.className)} />
              {!isLast && (
                <div className="w-px flex-1 bg-[var(--e-border-secondary)]" />
              )}
            </div>

            {/* Content */}
            <div className={cn('pb-4', isLast && 'pb-2')}>
              <div className="flex items-baseline gap-2">
                <span className="text-[12px] font-semibold text-[var(--e-text-primary)]">
                  {step.stage}
                </span>
                {step.module_id && (
                  <span className="rounded bg-[var(--e-purple-50)] px-1 py-0.5 font-mono text-[9px] text-[var(--e-purple-700)]">
                    {step.module_id}
                  </span>
                )}
                <span className={cn(
                  'text-[10px] font-semibold',
                  step.status === 'PASSED' && 'text-[var(--e-green-600)]',
                  step.status === 'FAILED' && 'text-[var(--e-red-600)]',
                  step.status === 'RUNNING' && 'text-[var(--e-purple-500)]',
                  step.status !== 'PASSED' && step.status !== 'FAILED' && step.status !== 'RUNNING' && 'text-[var(--e-text-muted)]',
                )}>
                  {step.status}
                </span>
                {step.duration_ms !== null && (
                  <span className="font-mono text-[10px] tabular-nums text-[var(--e-text-muted)]">
                    {formatDuration(step.duration_ms)}
                  </span>
                )}
              </div>
              {step.message && (
                <div className="mt-0.5 text-[11px] leading-4 text-[var(--e-text-secondary)]">
                  {step.message}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Module outputs panel (expanded detail) ──────────────────────────────

function TheatreOutputCard({ output }: { output: EnvironmentRunModuleOutput }) {
  const data = output.output_json as TheatreEvaluationOutput;
  return (
    <div className="overflow-hidden rounded-md border border-[var(--e-purple-200)] bg-[var(--e-bg-card)]">
      <div className="flex items-center gap-2 border-b border-[var(--e-border-secondary)] bg-[var(--e-purple-50)] px-3 py-2">
        <Box className="h-3.5 w-3.5 text-[var(--e-purple-500)]" />
        <span className="text-[11px] font-semibold text-[var(--e-purple-700)]">
          Theatre Evaluation
        </span>
        {data.is_primary && (
          <span className="rounded border border-[var(--e-purple-200)] bg-[var(--e-bg-card)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.04em] text-[var(--e-purple-500)]">
            Primary
          </span>
        )}
        <span className="ml-auto font-mono text-[10px] text-[var(--e-text-muted)]">
          {output.module_id}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 px-3 py-2.5 text-[11px] sm:grid-cols-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Construct</div>
          <div className="font-mono font-medium text-[var(--e-text-primary)]">{data.construct_slug}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Composite</div>
          <div className="font-mono font-semibold tabular-nums text-[var(--e-text-primary)]">{data.composite_score.toFixed(3)}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Brier</div>
          <div className="font-mono font-semibold tabular-nums text-[var(--e-text-primary)]">
            {data.brier_score !== null ? data.brier_score.toFixed(3) : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Checks</div>
          <div className="flex items-center gap-1.5">
            <span className="font-mono font-medium text-[var(--e-green-600)]">{data.checks_passed}</span>
            <span className="text-[var(--e-text-disabled)]">/</span>
            <span className="font-mono font-medium text-[var(--e-text-primary)]">{data.checks_executed}</span>
            {data.checks_failed > 0 && (
              <span className="font-mono font-medium text-[var(--e-red-600)]">({data.checks_failed} failed)</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Replay</div>
          <div className={cn(
            'font-medium',
            data.replay_verified ? 'text-[var(--e-green-600)]' : 'text-[var(--e-red-600)]',
          )}>
            {data.replay_verified ? 'Verified' : 'Diverged'} ({data.replay_count})
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Tier</div>
          <div className="font-medium text-[var(--e-text-secondary)]">{data.verification_tier}</div>
        </div>
      </div>
    </div>
  );
}

function ForgeOutputCard({ output }: { output: EnvironmentRunModuleOutput }) {
  const data = output.output_json as ForgeIntakeOutput;
  const statusColor =
    data.status === 'PROCESSED' ? 'text-[var(--e-green-600)]'
    : data.status === 'VALIDATION_ERROR' ? 'text-[var(--e-red-600)]'
    : 'text-[var(--e-text-muted)]';
  return (
    <div className="overflow-hidden rounded-md border border-[oklch(0.54_0.17_255_/_0.20)] bg-[var(--e-bg-card)]">
      <div className="flex items-center gap-2 border-b border-[var(--e-border-secondary)] bg-[oklch(0.54_0.17_255_/_0.06)] px-3 py-2">
        <Download className="h-3.5 w-3.5 text-[oklch(0.54_0.17_255)]" />
        <span className="text-[11px] font-semibold text-[oklch(0.40_0.17_255)]">
          FORGE Intake
        </span>
        <span className="ml-auto font-mono text-[10px] text-[var(--e-text-muted)]">
          {output.module_id}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 px-3 py-2.5 text-[11px] sm:grid-cols-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Feed</div>
          <div className="font-mono font-medium text-[var(--e-text-primary)]">{data.feed_id}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Source</div>
          <div className="font-mono font-medium text-[var(--e-text-primary)]">{data.source_id}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Domain</div>
          <div className="font-medium text-[var(--e-text-primary)]">{data.domain}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Status</div>
          <div className={cn('font-semibold', statusColor)}>{data.status}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Received</div>
          <div className="font-mono font-semibold tabular-nums text-[var(--e-text-primary)]">{data.proposals_received}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Admitted</div>
          <div className="font-mono font-semibold tabular-nums text-[var(--e-green-600)]">{data.proposals_admitted}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Rejected</div>
          <div className={cn(
            'font-mono font-semibold tabular-nums',
            data.proposals_rejected > 0 ? 'text-[var(--e-red-600)]' : 'text-[var(--e-text-disabled)]',
          )}>
            {data.proposals_rejected}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Skipped</div>
          <div className="font-mono font-semibold tabular-nums text-[var(--e-text-muted)]">{data.proposals_skipped}</div>
        </div>
      </div>
    </div>
  );
}

function ScenarioOutputCard({ output }: { output: EnvironmentRunModuleOutput }) {
  const data = output.output_json as ScenarioPackEvaluationOutput;
  const statusColor =
    data.status === 'COMPLETED' ? 'text-[var(--e-green-600)]'
    : data.status === 'FAILED' ? 'text-[var(--e-red-600)]'
    : 'text-[var(--e-text-muted)]';
  const rewardColor =
    data.total_reward > 0 ? 'text-[var(--e-green-600)]'
    : data.total_reward < 0 ? 'text-[var(--e-red-600)]'
    : 'text-[var(--e-text-muted)]';
  return (
    <div className="overflow-hidden rounded-md border border-[oklch(0.45_0.18_275_/_0.20)] bg-[var(--e-bg-card)]">
      <div className="flex items-center gap-2 border-b border-[var(--e-border-secondary)] bg-[oklch(0.45_0.18_275_/_0.06)] px-3 py-2">
        <Dices className="h-3.5 w-3.5 text-[oklch(0.45_0.18_275)]" />
        <span className="text-[11px] font-semibold text-[oklch(0.35_0.18_275)]">
          Scenario Pack
        </span>
        <span className="ml-auto font-mono text-[10px] text-[var(--e-text-muted)]">
          {output.module_id}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 px-3 py-2.5 text-[11px] sm:grid-cols-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Template</div>
          <div className="font-mono font-medium text-[var(--e-text-primary)]">{data.template_name}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Mode</div>
          <div className="font-medium text-[var(--e-text-primary)]">{data.run_mode}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Seed</div>
          <div className="font-mono font-medium tabular-nums text-[var(--e-text-secondary)]">{data.environment_seed}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Status</div>
          <div className={cn('font-semibold', statusColor)}>{data.status}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Checkpoints</div>
          <div className="font-mono font-semibold tabular-nums text-[var(--e-text-primary)]">
            {data.checkpoints_resolved}/{data.checkpoints_total}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Total Reward</div>
          <div className={cn('font-mono font-semibold tabular-nums', rewardColor)}>
            {data.total_reward.toFixed(4)}
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentOutputCard({ output }: { output: EnvironmentRunModuleOutput }) {
  const data = output.output_json as AgentReadinessEvaluationOutput;
  const readinessColor =
    !data.is_alive ? 'text-[var(--e-red-600)]'
    : data.deployment_ready ? 'text-[var(--e-green-600)]'
    : 'text-[var(--e-orange-500)]';
  const readinessLabel =
    !data.is_alive ? 'DEAD'
    : data.deployment_ready ? 'READY'
    : 'LOW SANITY';
  const pnlColor =
    data.total_pnl_usd > 0 ? 'text-[var(--e-green-600)]'
    : data.total_pnl_usd < 0 ? 'text-[var(--e-red-600)]'
    : 'text-[var(--e-text-muted)]';
  return (
    <div className="overflow-hidden rounded-md border border-[oklch(0.55_0.15_145_/_0.20)] bg-[var(--e-bg-card)]">
      <div className="flex items-center gap-2 border-b border-[var(--e-border-secondary)] bg-[oklch(0.55_0.15_145_/_0.06)] px-3 py-2">
        <Bot className="h-3.5 w-3.5 text-[oklch(0.55_0.15_145)]" />
        <span className="text-[11px] font-semibold text-[oklch(0.40_0.15_145)]">
          Agent Actor
        </span>
        <span className="ml-auto font-mono text-[10px] text-[var(--e-text-muted)]">
          {output.module_id}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 px-3 py-2.5 text-[11px] sm:grid-cols-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Agent</div>
          <div className="font-medium text-[var(--e-text-primary)]">{data.agent_name}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Archetype</div>
          <div className="font-medium text-[var(--e-text-primary)]">{data.archetype}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Tier / Level</div>
          <div className="font-mono font-medium tabular-nums text-[var(--e-text-secondary)]">T{data.tier} / L{data.level}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Readiness</div>
          <div className={cn('font-semibold', readinessColor)}>{readinessLabel}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Sanity</div>
          <div className="font-mono font-semibold tabular-nums text-[var(--e-text-primary)]">
            {data.sanity}/{data.max_sanity}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Deployments</div>
          <div className="font-mono font-medium tabular-nums text-[var(--e-text-secondary)]">{data.active_deployments}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">PnL</div>
          <div className={cn('font-mono font-semibold tabular-nums', pnlColor)}>
            ${data.total_pnl_usd.toFixed(2)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Win Rate</div>
          <div className="font-mono font-medium tabular-nums text-[var(--e-text-secondary)]">
            {(data.win_rate * 100).toFixed(0)}% ({data.trades_count})
          </div>
        </div>
      </div>
    </div>
  );
}

const SEVERITY_COLORS: Record<string, string> = {
  INFO: 'text-[var(--e-text-muted)]',
  WATCH: 'text-[var(--e-orange-500)]',
  MATERIAL: 'text-[var(--e-red-600)]',
};

function ParadoxScanCard({ output }: { output: EnvironmentRunModuleOutput }) {
  const data = output.output_json as ParadoxScanOutput;
  const statusColor =
    data.scan_status === 'COMPLETED' && data.findings_count === 0
      ? 'text-[var(--e-green-600)]'
      : data.scan_status === 'ERROR'
        ? 'text-[var(--e-red-600)]'
        : data.severity_max === 'MATERIAL'
          ? 'text-[var(--e-red-600)]'
          : data.severity_max === 'WATCH'
            ? 'text-[var(--e-orange-500)]'
            : 'text-[var(--e-text-secondary)]';
  const statusLabel =
    data.scan_status === 'COMPLETED' && data.findings_count === 0
      ? 'CONSISTENT'
      : data.scan_status === 'COMPLETED'
        ? `${data.findings_count} FINDING${data.findings_count !== 1 ? 'S' : ''}`
        : data.scan_status;

  return (
    <div className="overflow-hidden rounded-md border border-[oklch(0.50_0.18_300_/_0.20)] bg-[var(--e-bg-card)]">
      <div className="flex items-center gap-2 border-b border-[var(--e-border-secondary)] bg-[oklch(0.50_0.18_300_/_0.06)] px-3 py-2">
        <ShieldCheck className="h-3.5 w-3.5 text-[oklch(0.50_0.18_300)]" />
        <span className="text-[11px] font-semibold text-[oklch(0.35_0.18_300)]">
          Paradox Scanner
        </span>
        <span className="ml-auto font-mono text-[10px] text-[var(--e-text-muted)]">
          {output.module_id}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 px-3 py-2.5 text-[11px] sm:grid-cols-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Status</div>
          <div className={cn('font-semibold', statusColor)}>{statusLabel}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Sources</div>
          <div className="font-mono font-medium tabular-nums text-[var(--e-text-secondary)]">
            {data.source_modules_scanned} module output{data.source_modules_scanned !== 1 ? 's' : ''}
          </div>
          {data.source_output_types.length > 0 && (
            <div className="font-mono text-[8px] text-[var(--e-text-muted)] leading-tight mt-0.5">
              {data.source_output_types.join(', ')}
            </div>
          )}
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Max Severity</div>
          <div className={cn('font-semibold', SEVERITY_COLORS[data.severity_max ?? ''] ?? 'text-[var(--e-text-muted)]')}>
            {data.severity_max ?? '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Version</div>
          <div className="font-mono text-[var(--e-text-muted)]">v{data.scanner_version}</div>
        </div>
      </div>
      {data.findings.length > 0 && (
        <div className="border-t border-[var(--e-border-secondary)] px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)] pb-1">
            Findings
          </div>
          <div className="space-y-1">
            {data.findings.map((f, i) => (
              <div key={i} className="flex items-start gap-2 text-[10px]">
                <span className={cn('shrink-0 font-semibold', SEVERITY_COLORS[f.severity] ?? 'text-[var(--e-text-muted)]')}>
                  {f.severity}
                </span>
                <span className="font-mono text-[var(--e-text-muted)]">{f.finding_type}</span>
                <span className="text-[var(--e-text-secondary)]">{f.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.skip_reason && (
        <div className="border-t border-[var(--e-border-secondary)] px-3 py-1.5 text-[10px] text-[var(--e-text-muted)]">
          {data.skip_reason}
        </div>
      )}
    </div>
  );
}

const OUTCOME_COLORS: Record<string, string> = {
  IMPROVED: 'text-[var(--e-green-600)]',
  REGRESSED: 'text-[var(--e-red-600)]',
  UNCHANGED: 'text-[var(--e-text-muted)]',
  NO_DATA: 'text-[var(--e-text-muted)]',
};

const OUTCOME_SYMBOLS: Record<string, string> = {
  IMPROVED: '+',
  REGRESSED: '-',
  UNCHANGED: '=',
  NO_DATA: '?',
};

function CalibrationCard({ output }: { output: EnvironmentRunModuleOutput }) {
  const data = output.output_json as CalibrationOutput;
  const statusColor =
    data.calibration_status === 'COMPLETED' && data.regressed_count === 0
      ? 'text-[var(--e-green-600)]'
      : data.calibration_status === 'COMPLETED' && data.regressed_count > 0
        ? 'text-[var(--e-orange-500)]'
        : data.calibration_status === 'ERROR'
          ? 'text-[var(--e-red-600)]'
          : 'text-[var(--e-text-secondary)]';
  const statusLabel =
    data.calibration_status === 'COMPLETED'
      ? data.primary_outcome ?? 'COMPLETED'
      : data.calibration_status;

  return (
    <div className="overflow-hidden rounded-md border border-[oklch(0.60_0.14_60_/_0.20)] bg-[var(--e-bg-card)]">
      <div className="flex items-center gap-2 border-b border-[var(--e-border-secondary)] bg-[oklch(0.60_0.14_60_/_0.06)] px-3 py-2">
        <Activity className="h-3.5 w-3.5 text-[oklch(0.60_0.14_60)]" />
        <span className="text-[11px] font-semibold text-[oklch(0.40_0.14_60)]">
          Calibration
        </span>
        <span className="ml-auto font-mono text-[10px] text-[var(--e-text-muted)]">
          {output.module_id}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 px-3 py-2.5 text-[11px] sm:grid-cols-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Outcome</div>
          <div className={cn('font-semibold', statusColor)}>{statusLabel}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Target Type</div>
          <div className="font-mono text-[var(--e-text-secondary)]">{data.target_output_type || '—'}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Baseline</div>
          <div className="truncate font-mono text-[10px] text-[var(--e-text-muted)]">
            {data.baseline_source === 'none' ? '—' : data.baseline_source}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">Metrics</div>
          <div className="font-mono font-medium tabular-nums text-[var(--e-text-secondary)]">
            {data.metrics_compared > 0
              ? `${data.improved_count}↑ ${data.regressed_count}↓ ${data.unchanged_count}=`
              : '—'}
          </div>
        </div>
      </div>
      {data.comparisons.length > 0 && (
        <div className="border-t border-[var(--e-border-secondary)] px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)] pb-1">
            Metric Details
          </div>
          <div className="space-y-1">
            {data.comparisons.map((c, i) => (
              <div key={i} className="flex items-center gap-3 text-[10px]">
                <span className={cn('w-3 shrink-0 text-center font-bold', OUTCOME_COLORS[c.outcome])}>
                  {OUTCOME_SYMBOLS[c.outcome]}
                </span>
                <span className="w-28 shrink-0 font-mono text-[var(--e-text-secondary)]">{c.metric}</span>
                <span className="font-mono tabular-nums text-[var(--e-text-muted)]">
                  {c.baseline_value !== null ? c.baseline_value.toFixed(4) : '—'}
                </span>
                <span className="text-[var(--e-text-muted)]">→</span>
                <span className="font-mono tabular-nums text-[var(--e-text-primary)]">
                  {c.current_value !== null ? c.current_value.toFixed(4) : '—'}
                </span>
                {c.delta !== null && (
                  <span className={cn('font-mono tabular-nums', OUTCOME_COLORS[c.outcome])}>
                    ({c.delta >= 0 ? '+' : ''}{c.delta.toFixed(4)})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {data.skip_reason && (
        <div className="border-t border-[var(--e-border-secondary)] px-3 py-1.5 text-[10px] text-[var(--e-text-muted)]">
          {data.skip_reason}
        </div>
      )}
    </div>
  );
}

function ModuleOutputsPanel({ runId, runStatus }: { runId: string; runStatus: EnvironmentRunStatus }) {
  const isTerminal = ENV_TERMINAL_STATUSES.includes(runStatus);
  const { outputs, isLoading } = useRunModuleOutputs(runId, runStatus);

  // Don't show panel for non-terminal runs (outputs not yet written)
  if (!isTerminal) return null;

  if (isLoading) {
    return (
      <div className="mx-4 mt-3">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)] pb-2">
          Module Outputs
        </div>
        <div className="flex items-center gap-2 py-2 text-[11px] text-[var(--e-text-muted)]">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading module outputs…
        </div>
      </div>
    );
  }

  if (outputs.length === 0) {
    return (
      <div className="mx-4 mt-3">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)] pb-2">
          Module Outputs
        </div>
        <div className="rounded-md border border-[var(--e-border-secondary)] bg-[var(--e-bg-card)] px-3 py-2 text-[11px] text-[var(--e-text-muted)]">
          No module outputs — modules executed with simulation only
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 mt-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)] pb-2">
        Module Outputs
        <span className="ml-2 normal-case tracking-normal font-normal">
          ({outputs.length})
        </span>
      </div>
      <div className="space-y-2">
        {outputs.map((output) => {
          if (output.output_type === 'theatre_evaluation') {
            return <TheatreOutputCard key={output.output_id} output={output} />;
          }
          if (output.output_type === 'forge_intake') {
            return <ForgeOutputCard key={output.output_id} output={output} />;
          }
          if (output.output_type === 'scenario_pack_evaluation') {
            return <ScenarioOutputCard key={output.output_id} output={output} />;
          }
          if (output.output_type === 'agent_readiness_evaluation') {
            return <AgentOutputCard key={output.output_id} output={output} />;
          }
          if (output.output_type === 'paradox_scan') {
            return <ParadoxScanCard key={output.output_id} output={output} />;
          }
          if (output.output_type === 'calibration') {
            return <CalibrationCard key={output.output_id} output={output} />;
          }
          // Unknown output type — show raw JSON as fallback
          return (
            <div key={output.output_id} className="rounded-md border border-[var(--e-border-secondary)] bg-[var(--e-bg-card)] px-3 py-2">
              <div className="text-[10px] font-semibold text-[var(--e-text-muted)]">
                {output.output_type} — {output.module_id}
              </div>
              <pre className="mt-1 overflow-auto font-mono text-[10px] text-[var(--e-text-secondary)]">
                {JSON.stringify(output.output_json, null, 2)}
              </pre>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Attestation proof card ───────────────────────────────────────────────

function AttestationProofCard({ attestationId }: { attestationId: string }) {
  const { data: att, isLoading } = useEnvironmentAttestation(attestationId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 border-t border-[var(--e-border-secondary)] bg-[color:oklch(0.545_0.170_152_/_0.04)] px-3 py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--e-green-600)]" />
        <span className="text-[11px] text-[var(--e-text-muted)]">Loading attestation…</span>
      </div>
    );
  }

  if (!att) {
    return (
      <div className="flex items-center gap-2 border-t border-[var(--e-border-secondary)] bg-[color:oklch(0.545_0.170_152_/_0.04)] px-3 py-2">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-[var(--e-green-600)]" />
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-green-600)]">
            Environment Attestation
          </div>
          <div className="truncate font-mono text-[11px] text-[var(--e-text-secondary)] select-all">
            {attestationId}
          </div>
        </div>
      </div>
    );
  }

  const isRevoked = att.revoked_at !== null;
  const isExpired = att.is_expired;
  const isCurrent = att.is_current;
  const accentColor = isRevoked
    ? 'var(--e-red-600)'
    : isExpired
      ? 'var(--e-yellow-600)'
      : 'var(--e-green-600)';
  const bgTint = isRevoked
    ? 'bg-[color:oklch(0.545_0.185_25_/_0.04)]'
    : isExpired
      ? 'bg-[color:oklch(0.60_0.12_85_/_0.04)]'
      : 'bg-[color:oklch(0.545_0.170_152_/_0.04)]';

  return (
    <div className={cn('border-t border-[var(--e-border-secondary)]', bgTint)}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2">
        {isRevoked
          ? <XCircle className="h-3.5 w-3.5 shrink-0" style={{ color: accentColor }} />
          : <ShieldCheck className="h-3.5 w-3.5 shrink-0" style={{ color: accentColor }} />
        }
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.06em]" style={{ color: accentColor }}>
              Environment Attestation
            </span>
            {isRevoked && (
              <span className="rounded bg-[var(--e-red-50)] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[var(--e-red-600)]">
                Revoked
              </span>
            )}
            {isExpired && !isRevoked && (
              <span className="rounded bg-[color:oklch(0.95_0.04_85)] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[color:oklch(0.55_0.14_65)]">
                Expired
              </span>
            )}
            {isCurrent && (
              <span className="rounded bg-[color:oklch(0.95_0.04_152)] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[var(--e-green-600)]">
                Current
              </span>
            )}
          </div>
          <div className="truncate font-mono text-[11px] text-[var(--e-text-secondary)] select-all">
            {att.attestation_id}
          </div>
        </div>
        <div className="text-right text-[10px] text-[var(--e-text-muted)]">
          <div>
            {new Date(att.issued_at).toLocaleDateString(undefined, {
              month: 'short', day: 'numeric', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </div>
          {att.expires_at && (
            <div className={isExpired ? 'text-[color:oklch(0.55_0.14_65)] font-medium' : ''}>
              {isExpired ? 'Expired' : 'Expires'}{' '}
              {new Date(att.expires_at).toLocaleDateString(undefined, {
                month: 'short', day: 'numeric', year: 'numeric',
              })}
            </div>
          )}
        </div>
      </div>

      {/* Revocation banner — only when revoked */}
      {isRevoked && att.revoked_at && (
        <div className="flex items-start gap-2 border-t border-[color:oklch(0.545_0.185_25_/_0.18)] bg-[var(--e-red-50)] px-3 py-2">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[var(--e-red-600)] mt-0.5" />
          <div className="min-w-0 text-[11px]">
            <div className="font-semibold text-[var(--e-red-600)]">
              Revoked {new Date(att.revoked_at).toLocaleDateString(undefined, {
                month: 'short', day: 'numeric', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
            </div>
            {att.revocation_reason && (
              <div className="mt-0.5 text-[var(--e-text-secondary)]">
                {att.revocation_reason}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Construct + scorer info */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-[var(--e-border-secondary)] px-3 py-1.5 text-[10px]">
        <span className="text-[var(--e-text-muted)]">
          Construct: <span className="font-semibold text-[var(--e-text-primary)]">{att.construct_id}</span>
          <span className="ml-1 text-[var(--e-text-muted)]">v{att.construct_version}</span>
        </span>
        <span className="text-[var(--e-text-muted)]">
          Scorer: <span className="font-mono text-[var(--e-text-secondary)]">{att.scorer_version}</span>
        </span>
        <span className="text-[var(--e-text-muted)]">
          Attestation: <span className="font-mono text-[var(--e-text-secondary)]">v{att.attestation_version}</span>
        </span>
      </div>

      {/* Check summary + scores */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--e-border-secondary)] px-3 py-1.5 text-[11px]">
        <span className="font-semibold text-[var(--e-text-primary)]">
          {att.checks_executed} checks
        </span>
        <span className="flex items-center gap-1 text-[var(--e-green-600)]">
          <CheckCircle2 className="h-3 w-3" />
          {att.checks_passed} passed
        </span>
        {att.checks_failed > 0 && (
          <span className="flex items-center gap-1 text-[var(--e-red-600)]">
            <XCircle className="h-3 w-3" />
            {att.checks_failed} failed
          </span>
        )}
        {att.composite_score != null && (
          <span className="font-mono text-[10px] tabular-nums text-[var(--e-text-muted)]">
            Composite: {att.composite_score.toFixed(3)}
          </span>
        )}
        {att.brier_score != null && (
          <span className="font-mono text-[10px] tabular-nums text-[var(--e-text-muted)]">
            Brier: {att.brier_score.toFixed(3)}
          </span>
        )}
      </div>

      {/* Replay verification */}
      <div className="flex items-center gap-2 border-t border-[var(--e-border-secondary)] px-3 py-1.5">
        <RefreshCw className={cn(
          'h-3 w-3 shrink-0',
          att.replay_verified ? 'text-[var(--e-green-600)]' : 'text-[var(--e-red-600)]',
        )} />
        <span className={cn(
          'text-[11px] font-medium',
          att.replay_verified ? 'text-[var(--e-green-600)]' : 'text-[var(--e-red-600)]',
        )}>
          {att.replay_verified ? 'Replay verified' : 'Replay failed'}
          <span className="ml-1 font-normal text-[var(--e-text-muted)]">
            ({att.replay_count} {att.replay_count === 1 ? 'replay' : 'replays'})
          </span>
        </span>
      </div>

      {/* Four-hash proof chain */}
      <div className="divide-y divide-[var(--e-border-secondary)] border-t border-[var(--e-border-secondary)]">
        {[
          { label: 'Commitment', hash: att.commitment_hash, hint: 'pre-evaluation scope' },
          { label: 'Ground Truth', hash: att.ground_truth_hash, hint: 'truth source' },
          { label: 'Evidence', hash: att.evidence_hash, hint: 'post-execution fingerprint' },
          { label: 'Receipt', hash: att.receipt_hash, hint: 'final run result' },
        ].map(({ label, hash, hint }) => (
          <div key={label} className="flex items-baseline gap-2 px-3 py-1">
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)] w-[90px]">
              {label}
            </span>
            <span className="truncate font-mono text-[10px] text-[var(--e-text-secondary)] select-all">
              {hash}
            </span>
            <span className="shrink-0 text-[9px] text-[var(--e-text-muted)]">
              {hint}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Run row ──────────────────────────────────────────────────────────────

function RunRow({
  run,
  onCancel,
  onRetry,
  isExpanded,
  onToggle,
}: {
  run: EnvironmentRun;
  onCancel: (runId: string) => void;
  onRetry: (runId: string) => void;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const isActive =
    run.status !== 'COMPLETED' &&
    run.status !== 'FAILED' &&
    run.status !== 'CANCELLED';

  const colCount = 8;

  return (
    <>
      <tr
        className={cn(
          'align-top transition hover:bg-[var(--e-bg-hover)] cursor-pointer',
          isExpanded
            ? 'bg-[var(--e-bg-hover)]'
            : 'border-b border-[var(--e-border-secondary)] last:border-b-0',
        )}
        onClick={onToggle}
      >
        {/* Run ID */}
        <td className="px-4 py-4">
          <div className="flex items-center gap-1.5">
            {isExpanded
              ? <ChevronDown className="h-3 w-3 shrink-0 text-[var(--e-text-muted)]" />
              : <ChevronRight className="h-3 w-3 shrink-0 text-[var(--e-text-muted)]" />
            }
            <div>
              <span className="font-mono text-[12px] font-semibold text-[var(--e-purple-700)]">
                {run.run_id.slice(0, 16)}
              </span>
              <div className="mt-0.5 font-mono text-[10px] text-[var(--e-text-muted)]">
                {formatRelativeTime(run.created_at)}
              </div>
            </div>
          </div>
        </td>

        {/* Construct / Theatre */}
        <td className="px-4 py-4">
          <div className="text-[13px] font-semibold text-[var(--e-text-primary)]">
            {run.construct_id}
          </div>
          <Link
            to={`/theatre/${run.theatre_id}`}
            className="text-[11px] font-medium text-[var(--e-text-muted)] no-underline transition hover:text-[var(--e-purple-700)]"
            onClick={(e) => e.stopPropagation()}
          >
            {run.theatre_id}
          </Link>
        </td>

        {/* Status */}
        <td className="px-4 py-4">
          <StatusBadge status={run.status} />
          {run.current_stage && isActive && (
            <div className="mt-1 font-mono text-[10px] text-[var(--e-text-muted)]">
              Stage: {run.current_stage}
            </div>
          )}
        </td>

        {/* Progress */}
        <td className="px-4 py-4" style={{ minWidth: 140 }}>
          <ProgressBar
            progress={run.progress}
            total={run.total_steps}
            status={run.status}
          />
          <div className="mt-1 font-mono text-[10px] text-[var(--e-text-muted)]">
            {run.progress}/{run.total_steps} steps
          </div>
        </td>

        {/* Modules */}
        <td className="px-4 py-4 text-center">
          <span className="font-mono text-[13px] font-semibold tabular-nums text-[var(--e-text-primary)]">
            {run.modules_active}/{run.modules_total}
          </span>
        </td>

        {/* Score */}
        <td className="px-4 py-4 text-right">
          {run.composite_score !== null ? (
            <span className="font-mono text-[13px] font-semibold tabular-nums text-[var(--e-text-primary)]">
              {run.composite_score.toFixed(3)}
            </span>
          ) : (
            <span className="font-mono text-[11px] text-[var(--e-text-disabled)]">
              —
            </span>
          )}
          {run.brier_score !== null && (
            <div className="font-mono text-[10px] text-[var(--e-text-muted)]">
              Brier: {run.brier_score.toFixed(3)}
            </div>
          )}
          {run.verification_level === 'PROVISIONAL' && run.composite_score !== null && (
            <div className="mt-1 inline-flex items-center gap-1 rounded border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em] text-[var(--e-text-muted)]">
              Provisional
            </div>
          )}
          {run.verification_level === 'EVALUATED' && run.composite_score !== null && (
            <div className="mt-1 inline-flex items-center gap-1 rounded border border-[color:oklch(0.708_0.136_62_/_0.20)] bg-[color:oklch(0.708_0.136_62_/_0.06)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em] text-[var(--e-orange-600)]">
              <CheckCircle2 className="h-2.5 w-2.5" />
              Evaluated
            </div>
          )}
          {run.verification_level === 'CERTIFIED' && run.attestation_id && (
            <div className="mt-1 inline-flex items-center gap-1 rounded border border-[color:oklch(0.545_0.170_152_/_0.18)] bg-[var(--e-green-50)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em] text-[var(--e-green-600)]">
              <ShieldCheck className="h-2.5 w-2.5" />
              Attested
            </div>
          )}
        </td>

        {/* Duration */}
        <td className="px-4 py-4 text-right">
          <span className="font-mono text-[12px] tabular-nums text-[var(--e-text-secondary)]">
            {formatDuration(run.duration_ms)}
          </span>
        </td>

        {/* Actions */}
        <td className="px-4 py-4">
          <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
            {run.status === 'FAILED' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRetry(run.run_id)}
                className="h-7 gap-1 border-[var(--e-border-primary)] bg-[var(--e-bg-card)] px-3 text-[11px] font-semibold text-[var(--e-text-secondary)] hover:bg-[var(--e-bg-hover)] hover:text-[var(--e-text-primary)]"
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </Button>
            )}
            {isActive && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onCancel(run.run_id)}
                className="h-7 gap-1 border border-[color:oklch(0.545_0.185_25_/_0.18)] bg-[var(--e-red-50)] px-3 text-[11px] font-semibold text-[var(--e-red-600)] hover:bg-[color:oklch(0.545_0.185_25_/_0.14)]"
              >
                <XCircle className="h-3 w-3" />
                Cancel
              </Button>
            )}
            {run.certificate_id && (
              <Link
                to={`/verify?certificate=${encodeURIComponent(run.certificate_id)}`}
                className="inline-flex items-center gap-1 rounded-md border border-[color:oklch(0.545_0.170_152_/_0.18)] bg-[var(--e-green-50)] px-3 py-1.5 text-[11px] font-semibold text-[var(--e-green-600)] no-underline transition hover:bg-[color:oklch(0.545_0.170_152_/_0.14)]"
              >
                <ShieldCheck className="h-3 w-3" />
                Certificate
              </Link>
            )}
            <Link
              to={`/theatres/${run.theatre_id}/build`}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] px-3 py-1.5 text-[11px] font-semibold text-[var(--e-text-secondary)] no-underline transition hover:bg-[var(--e-bg-hover)] hover:text-[var(--e-text-primary)]"
            >
              <ExternalLink className="h-3 w-3" />
              Build
            </Link>
          </div>
        </td>
      </tr>

      {/* Expanded step detail */}
      {isExpanded && (
        <tr className="border-b border-[var(--e-border-secondary)] last:border-b-0">
          <td colSpan={colCount} className="bg-[var(--e-bg-sunken)] py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)] px-4 pb-2">
              Execution Stages
            </div>
            <StepTimeline runId={run.run_id} runStatus={run.status} />
            <ModuleOutputsPanel runId={run.run_id} runStatus={run.status} />
            {run.check_results_json && (
              <div className="mx-4 mt-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)] pb-2">
                  Evaluation Evidence
                </div>
                <div className="overflow-hidden rounded-md border border-[color:oklch(0.708_0.136_62_/_0.20)] bg-[var(--e-bg-card)]">
                  {/* Summary counts */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-[var(--e-border-secondary)] bg-[color:oklch(0.708_0.136_62_/_0.04)] px-3 py-2 text-[11px]">
                    <span className="font-semibold text-[var(--e-text-primary)]">
                      {run.check_results_json.total_executed} checks executed
                    </span>
                    <span className="flex items-center gap-1 text-[var(--e-green-600)]">
                      <CheckCircle2 className="h-3 w-3" />
                      {run.check_results_json.total_passed} passed
                    </span>
                    {run.check_results_json.total_failed > 0 && (
                      <span className="flex items-center gap-1 text-[var(--e-red-600)]">
                        <XCircle className="h-3 w-3" />
                        {run.check_results_json.total_failed} failed
                      </span>
                    )}
                    {run.check_results_json.total_skipped > 0 && (
                      <span className="text-[var(--e-text-muted)]">
                        {run.check_results_json.total_skipped} skipped
                      </span>
                    )}
                    {run.check_results_json.has_critical_failures && (
                      <span className="flex items-center gap-1 rounded bg-[var(--e-red-50)] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[var(--e-red-600)]">
                        <AlertTriangle className="h-3 w-3" />
                        Critical Failures
                      </span>
                    )}
                  </div>
                  {/* Individual checks */}
                  <div className="divide-y divide-[var(--e-border-secondary)]">
                    {run.check_results_json.checks.map((check) => (
                      <div
                        key={check.check_id}
                        className="flex items-center gap-3 px-3 py-1.5"
                      >
                        {check.status === 'PASSED' ? (
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[var(--e-green-600)]" />
                        ) : check.status === 'FAILED' ? (
                          <XCircle className="h-3.5 w-3.5 shrink-0 text-[var(--e-red-600)]" />
                        ) : (
                          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-[var(--e-text-muted)]" />
                        )}
                        <span className="min-w-0 font-mono text-[11px] font-semibold text-[var(--e-text-primary)]">
                          {check.check_type}
                        </span>
                        {check.evidence &&
                          typeof check.evidence === 'object' &&
                          'computed_brier' in check.evidence && (
                            <span className="font-mono text-[10px] tabular-nums text-[var(--e-text-muted)]">
                              Brier: {Number(check.evidence.computed_brier).toFixed(3)}
                            </span>
                          )}
                        {check.reason && (
                          <span className="ml-auto truncate text-[10px] text-[var(--e-text-muted)]" title={check.reason}>
                            {check.reason}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Proof hashes */}
                  {(run.commitment_hash || run.ground_truth_hash || run.evidence_hash || run.replay_count != null) && (
                    <div className="divide-y divide-[var(--e-border-secondary)] border-t border-[var(--e-border-secondary)] bg-[color:oklch(0.708_0.136_62_/_0.04)]">
                      {run.commitment_hash && (
                        <div className="flex items-center gap-2 px-3 py-2">
                          <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-[var(--e-purple-500)]" />
                          <div className="min-w-0">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                              Commitment Hash
                              <span className="ml-2 normal-case tracking-normal font-normal">
                                (pre-evaluation scope)
                              </span>
                            </div>
                            <div className="truncate font-mono text-[11px] text-[var(--e-text-secondary)] select-all">
                              {run.commitment_hash}
                            </div>
                          </div>
                        </div>
                      )}
                      {run.ground_truth_hash && (
                        <div className="flex items-center gap-2 px-3 py-2">
                          <Database className="h-3.5 w-3.5 shrink-0 text-[var(--e-green-600)]" />
                          <div className="min-w-0">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                              Ground Truth Hash
                              {run.ground_truth_source && (
                                <span className="ml-2 normal-case tracking-normal font-normal">
                                  ({run.ground_truth_source.replace(/_/g, ' ')})
                                </span>
                              )}
                            </div>
                            <div className="truncate font-mono text-[11px] text-[var(--e-text-secondary)] select-all">
                              {run.ground_truth_hash}
                            </div>
                          </div>
                        </div>
                      )}
                      {run.evidence_hash && (
                        <div className="flex items-center gap-2 px-3 py-2">
                          <Fingerprint className="h-3.5 w-3.5 shrink-0 text-[var(--e-orange-600)]" />
                          <div className="min-w-0">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                              Evidence Hash
                              <span className="ml-2 normal-case tracking-normal font-normal">
                                (post-execution fingerprint)
                              </span>
                            </div>
                            <div className="truncate font-mono text-[11px] text-[var(--e-text-secondary)] select-all">
                              {run.evidence_hash}
                            </div>
                          </div>
                        </div>
                      )}
                      {run.replay_count != null && (
                        <div className="flex items-center gap-2 px-3 py-2">
                          <RefreshCw className={cn(
                            'h-3.5 w-3.5 shrink-0',
                            run.replay_verified
                              ? 'text-[var(--e-green-600)]'
                              : 'text-[var(--e-red-600)]',
                          )} />
                          <div className="min-w-0">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                              Replay Verification
                              <span className="ml-2 normal-case tracking-normal font-normal">
                                ({run.replay_count} {run.replay_count === 1 ? 'replay' : 'replays'})
                              </span>
                            </div>
                            <div className={cn(
                              'text-[11px] font-medium',
                              run.replay_verified
                                ? 'text-[var(--e-green-600)]'
                                : 'text-[var(--e-red-600)]',
                            )}>
                              {run.replay_verified
                                ? 'All evidence hashes match — deterministic'
                                : 'Evidence hash diverged — not reproducible'}
                            </div>
                          </div>
                        </div>
                      )}
                      {run.attestation_id && (
                        <AttestationProofCard attestationId={run.attestation_id} />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {run.error && (
              <div className="mx-4 mt-2 rounded-md border border-[color:oklch(0.545_0.185_25_/_0.18)] bg-[var(--e-red-50)] px-3 py-2 text-[11px] text-[var(--e-red-600)]">
                <span className="font-semibold">Error:</span> {run.error}
              </div>
            )}
            {run.receipt_hash && (
              <div className="mx-4 mt-2 flex items-center gap-2 rounded-md border border-[var(--e-border-secondary)] bg-[var(--e-bg-card)] px-3 py-2">
                <Fingerprint className="h-3.5 w-3.5 shrink-0 text-[var(--e-text-muted)]" />
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
                    Receipt Hash
                    {run.verification_level && (
                      <span className="ml-2 normal-case tracking-normal font-normal">
                        ({run.verification_level.toLowerCase()})
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[11px] text-[var(--e-text-secondary)] select-all">
                    {run.receipt_hash}
                  </div>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Right rail ───────────────────────────────────────────────────────────

function RunTimeline({ runs }: { runs: EnvironmentRun[] }) {
  const recent = runs.slice(0, 8);
  return (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] shadow-[var(--e-shadow-xs)]">
        <div className="border-b border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
            Recent Activity
          </h3>
        </div>
        <div className="px-4 py-3">
          {recent.length > 0 ? (
            recent.map((run) => {
              const config = STATUS_CONFIG[run.status];
              return (
                <div
                  key={run.run_id}
                  className="flex items-start gap-2.5 border-b border-[var(--e-border-secondary)] py-2.5 last:border-b-0"
                >
                  <span
                    className={cn(
                      'mt-1 h-2 w-2 shrink-0 rounded-full',
                      config.dotClass,
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12px] font-semibold text-[var(--e-text-primary)]">
                      {run.construct_id}
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'text-[10px] font-semibold',
                          config.textClass,
                        )}
                      >
                        {config.label}
                      </span>
                      <span className="font-mono text-[10px] text-[var(--e-text-muted)]">
                        {formatRelativeTime(run.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-[12px] text-[var(--e-text-muted)]">
              No runs yet.
            </div>
          )}
        </div>
      </section>

      <section className="overflow-hidden rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] shadow-[var(--e-shadow-xs)]">
        <div className="border-b border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
            Score Trend
          </h3>
        </div>
        <div className="px-4 py-4">
          {(() => {
            const scored = runs
              .filter((r) => r.composite_score !== null)
              .slice(0, 5);
            if (scored.length === 0) {
              return (
                <div className="text-[12px] text-[var(--e-text-muted)]">
                  No scored runs yet.
                </div>
              );
            }
            return (
              <div className="space-y-2">
                {scored.map((run) => (
                  <div
                    key={run.run_id}
                    className="flex items-center gap-3"
                  >
                    <span className="w-16 truncate text-[11px] text-[var(--e-text-secondary)]">
                      {run.run_id.slice(-6)}
                    </span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--e-bg-sunken)]">
                      <div
                        className="h-full rounded-full bg-[var(--e-purple-500)]"
                        style={{
                          width: `${(run.composite_score ?? 0) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="w-10 text-right font-mono text-[10px] tabular-nums text-[var(--e-text-muted)]">
                      {run.composite_score?.toFixed(3)}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function EnvironmentMonitorPage() {
  const { theatreId } = useParams();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const { theatre } = useTheatreDetail(theatreId ?? null, { includeInvestigationContext: true });

  const { runs, total, isLoading, error, cancelRun, retryRun } =
    useEnvironmentRuns(
      theatreId ? { theatre_id: theatreId } : {},
    );

  // ── Derived counts ──────────────────────────────────────────────────
  const activeCount = runs.filter(
    (r) =>
      r.status !== 'COMPLETED' &&
      r.status !== 'FAILED' &&
      r.status !== 'CANCELLED',
  ).length;
  const completedCount = runs.filter((r) => r.status === 'COMPLETED').length;
  const failedCount = runs.filter((r) => r.status === 'FAILED').length;

  const avgScore = useMemo(() => {
    const scored = runs.filter((r) => r.composite_score !== null);
    if (scored.length === 0) return null;
    const sum = scored.reduce((acc, r) => acc + (r.composite_score ?? 0), 0);
    return (sum / scored.length).toFixed(3);
  }, [runs]);

  // ── Client-side filter ──────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (statusFilter === 'all') return runs;
    if (statusFilter === 'active') {
      return runs.filter(
        (r) =>
          r.status !== 'COMPLETED' &&
          r.status !== 'FAILED' &&
          r.status !== 'CANCELLED',
      );
    }
    if (statusFilter === 'completed') {
      return runs.filter((r) => r.status === 'COMPLETED');
    }
    return runs.filter(
      (r) => r.status === 'FAILED' || r.status === 'CANCELLED',
    );
  }, [runs, statusFilter]);

  const theatreLabel = useMemo(() => {
    if (!theatreId) return null;
    return theatreId.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }, [theatreId]);
  const linkedInvestigationId = theatre?.investigation_context?.investigation_summary?.investigation_id ?? null;
  const linkedInvestigationLabel = theatre?.investigation_context?.investigation_summary?.construct_id ?? 'Investigation';

  // ── Error state ─────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-7xl rounded-lg border border-[color:oklch(0.545_0.185_25_/_0.18)] bg-[var(--e-red-50)] px-5 py-4 text-[13px] text-[var(--e-red-600)] shadow-[var(--e-shadow-xs)]">
          Failed to load environment runs: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        {/* Breadcrumb / context */}
        <div className="flex items-center gap-2 text-[11px] font-medium text-[var(--e-text-muted)]">
          <Link
            to="/environment"
            className="no-underline transition hover:text-[var(--e-text-primary)]"
          >
            Environment
          </Link>
          <ArrowRight className="h-3 w-3" />
          {theatreLabel ? (
            <>
              <Link
                to={`/theatres/${theatreId}/build`}
                className="no-underline transition hover:text-[var(--e-text-primary)]"
              >
                {theatreLabel}
              </Link>
              <ArrowRight className="h-3 w-3" />
            </>
          ) : null}
          <span className="text-[var(--e-text-primary)]">Monitor</span>
        </div>

        <p className="max-w-4xl text-[15px] leading-6 text-[var(--e-text-secondary)]">
          {theatreLabel
            ? `Run history and results for ${theatreLabel} environment builds.`
            : 'Run history and results across all environment build pipelines.'}
        </p>

        {linkedInvestigationId && (
          <div>
            <Link
              to={`/workspace?investigation=${encodeURIComponent(linkedInvestigationId)}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] px-3 py-2 text-[12px] font-semibold text-[var(--e-text-secondary)] no-underline transition hover:bg-[var(--e-bg-hover)] hover:text-[var(--e-text-primary)]"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {linkedInvestigationLabel.length > 30
                ? linkedInvestigationLabel.slice(0, 27) + '...'
                : linkedInvestigationLabel}
            </Link>
          </div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
          <StatCard
            label="Total Runs"
            value={total}
            icon={Activity}
          />
          <StatCard
            label="Active"
            value={activeCount}
            icon={Play}
            tone={activeCount > 0 ? 'purple' : undefined}
          />
          <StatCard
            label="Completed"
            value={completedCount}
            icon={CheckCircle2}
            tone="success"
          />
          <StatCard
            label="Failed"
            value={failedCount}
            icon={AlertTriangle}
            tone={failedCount > 0 ? 'danger' : undefined}
          />
          <StatCard
            label="Avg Score"
            value={avgScore ?? '—'}
            icon={ShieldCheck}
          />
        </div>

        {/* Filter tabs */}
        <div className="inline-flex rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] p-1 shadow-[var(--e-shadow-xs)]">
          {(
            [
              { label: 'All', value: 'all' as StatusFilter, count: total },
              { label: 'Active', value: 'active' as StatusFilter, count: activeCount },
              { label: 'Completed', value: 'completed' as StatusFilter, count: completedCount },
              { label: 'Failed', value: 'failed' as StatusFilter, count: failedCount },
            ] as const
          ).map((tab) => (
            <Button
              key={tab.label}
              variant="ghost"
              size="sm"
              onClick={() => setStatusFilter(tab.value)}
              className={cn(
                'h-auto gap-2 rounded-md px-3 py-2 text-[13px] font-medium',
                statusFilter === tab.value
                  ? 'bg-[var(--e-purple-50)] text-[var(--e-purple-700)]'
                  : 'text-[var(--e-text-secondary)] hover:bg-[var(--e-bg-hover)] hover:text-[var(--e-text-primary)]',
              )}
            >
              {tab.label}
              <span className="font-mono text-[11px] text-[var(--e-text-muted)]">
                {tab.count}
              </span>
            </Button>
          ))}
        </div>

        {/* Main content */}
        {isLoading ? (
          <div className="flex items-center justify-center rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] px-6 py-16 shadow-[var(--e-shadow-xs)]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin text-[var(--e-text-muted)]" />
            <span className="text-[14px] text-[var(--e-text-muted)]">
              Loading runs…
            </span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] px-6 py-16 text-center shadow-[var(--e-shadow-xs)]">
            <Clock className="mb-3 h-8 w-8 text-[var(--e-text-disabled)]" />
            <div className="mb-1 text-[14px] font-semibold text-[var(--e-text-primary)]">
              No runs found
            </div>
            <div className="mb-4 text-[13px] text-[var(--e-text-muted)]">
              {statusFilter !== 'all'
                ? 'Try adjusting the filter or trigger a new run from the Build canvas.'
                : 'Trigger a run from the Environment Build canvas to see results here.'}
            </div>
            <Link
              to={
                theatreId
                  ? `/theatres/${theatreId}/build`
                  : '/environment'
              }
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--e-purple-500)] px-4 py-2 text-[12px] font-semibold text-white no-underline shadow-[var(--e-shadow-sm)] transition hover:bg-[var(--e-purple-400)]"
            >
              <Play className="h-3.5 w-3.5" />
              Open Build Canvas
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_300px]">
            {/* Table */}
            <div className="overflow-hidden rounded-xl border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] shadow-[var(--e-shadow-sm)]">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)]">
                      {[
                        'Run ID',
                        'Construct / Theatre',
                        'Status',
                        'Progress',
                        'Modules',
                        'Score',
                        'Duration',
                        'Actions',
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((run) => (
                      <RunRow
                        key={run.run_id}
                        run={run}
                        onCancel={cancelRun}
                        onRetry={retryRun}
                        isExpanded={expandedRunId === run.run_id}
                        onToggle={() =>
                          setExpandedRunId((prev) =>
                            prev === run.run_id ? null : run.run_id,
                          )
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between border-t border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-4 py-3 text-[12px] text-[var(--e-text-muted)]">
                <span>
                  Showing{' '}
                  <strong className="text-[var(--e-text-primary)]">
                    {filtered.length}
                  </strong>{' '}
                  of{' '}
                  <strong className="text-[var(--e-text-primary)]">
                    {total}
                  </strong>{' '}
                  runs
                </span>
                <span className="font-mono text-[10px]">
                  environment pipeline
                </span>
              </div>
            </div>

            {/* Right rail */}
            <div className="hidden lg:block">
              <RunTimeline runs={runs} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EnvironmentMonitorPage;
